import { UserIncentivizedRewardsDto } from '../../types/spool-local';
import { useWeb3React } from '@web3-react/core';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import Button from '../../components/Button';
import CustomModal from '../../components/CustomModal';
import CustomModalHeader from '../../components/CustomModal/CustomModalHeader';
import Loading from '../../components/Loading';
import { CLAIM_REWARDS_PENDING, REWARDS_CLAIMED } from '../../config/constants';
import appContext from '../../context/app/appContext';
import errorContext from '../../context/error/errorContext';
import modalContext from '../../context/modal/modalContext';
import { formatUSD } from '../../helpers/formats';
import useIsMounted from '../../hooks/useIsMounted';
import useIncentivizedEarnings from '../../hooks/web3/vault/useIncentivizedEarnings';
import useUserSpools from '../../hooks/web3/vault/useUserSpools';
import { sleep } from '../../web3/utils';

type UserSpoolRewards = {
    spoolName: string;
    spoolAddress: string;
    rewards: UserIncentivizedRewardsDto[];
};

const ClaimAllSpoolsIncentivizedRewards: React.FC = () => {
    const { account } = useWeb3React();
    const { openModal, closeModal, setModalData } = useContext(modalContext);

    const { sdk } = useContext(appContext);

    const { incentivizedEarnings, updateIncentivizedEarnings } =
        useIncentivizedEarnings();

    const { userSpools } = useUserSpools();

    const { setError } = useContext(errorContext);

    const [isLoading, setIsLoading] = useState(false);
    const [spoolRewards, setSpoolRewards] = useState<UserSpoolRewards[]>();

    const isMounted = useIsMounted();

    const onClose = () => closeModal('claimAllSpoolsIncentivizedRewards');

    const claim = async () => {
        try {
            if (!spoolRewards || !spoolRewards.length || !sdk || !incentivizedEarnings)
                return;

            setModalData((prev) => ({ ...prev, message: CLAIM_REWARDS_PENDING }));
            openModal('waitingModal');

            const claimRewards = await sdk!.spools.claimAllSpoolsIncentivizedRewards(
                account!
            );

            const receipt = await claimRewards.wait();

            await sleep(3000);

            await updateIncentivizedEarnings(incentivizedEarnings);

            onClose();
            setModalData((prev) => ({
                ...prev,
                status: REWARDS_CLAIMED,
                message: '',
                txHash: receipt.transactionHash,
            }));
            openModal('successModal');
        } catch (error) {
            setError(error);
        } finally {
            closeModal('waitingModal');
        }
    };

    const getSpoolRewards = useCallback(async () => {
        try {
            isMounted() && setIsLoading(true);
            const userSpoolRewards: UserSpoolRewards[] = await Promise.all(
                userSpools!
                    .filter(({ userData }) => userData?.userIncentivizedRewards.length)

                    .map(async ({ address, name }) => {
                        const rewards = await sdk!.spools.getUserIncentivizeRewards(
                            address,
                            account!.toLowerCase()
                        );

                        return {
                            spoolName: name,
                            spoolAddress: address,
                            rewards,
                        };
                    })
            );

            isMounted() && setSpoolRewards(userSpoolRewards);
        } catch (error) {
            console.log(error);
        } finally {
            isMounted() && setIsLoading(false);
        }
    }, [account, sdk, userSpools, isMounted]);

    useEffect(() => {
        if (!sdk || !userSpools) return;

        getSpoolRewards();
    }, [sdk, account, userSpools, getSpoolRewards]);

    return (
        <CustomModal
            modalName='claimAllSpoolsIncentivizedRewards'
            className='claim-all-spools-incentivized-modal'
        >
            <CustomModalHeader title='Claim All Rewards' onClose={onClose} />
            <div className='details'>
                <span>Details</span>
                <Loading isLoading={isLoading} text='Calculating your rewards...'>
                    {spoolRewards && spoolRewards?.length
                        ? spoolRewards.map(({ rewards, spoolName }) =>
                              rewards
                                  .filter(({ isRemoved }) => !isRemoved)
                                  .map(({ claimableAmount, token }, index) => (
                                      <div
                                          className='detail-container'
                                          key={`${spoolName}-${index}`}
                                      >
                                          <div className='name'>{spoolName}</div>
                                          <div className='value'>
                                              {formatUSD(claimableAmount || 0)}{' '}
                                              {token.symbol ?? 'Asset'}
                                          </div>
                                      </div>
                                  ))
                          )
                        : null}
                </Loading>
            </div>
            <div className='total'>
                <div className='text'>Total Available to Claim:</div>
                <div className='available'>
                    {incentivizedEarnings?.length ? (
                        incentivizedEarnings
                            .filter(({ isRemoved }) => !isRemoved)
                            .map(({ claimableAmount, token }, index) => (
                                <div className='claimable' key={index}>
                                    {formatUSD(claimableAmount || 0)}{' '}
                                    {token.symbol ?? 'Asset'}
                                </div>
                            ))
                    ) : (
                        <div className='claimable'>0</div>
                    )}
                </div>
            </div>
            <Button onClick={claim}>Claim</Button>
        </CustomModal>
    );
};

export default ClaimAllSpoolsIncentivizedRewards;
